// The pure rules of the schedule screens (spec 03 §4.6 to §4.8, P3-I-2): the
// two addresses, times in the facility's zone with its offset, where a block
// or a piece of working time sits on a lane, the closed time a block spans,
// overlapping blocks stacked into rows, the board's filters, an operation's
// slack, the capacity rows by work centre and the setup matrix's own checks
// before a save. No React and no network here, so each rule is unit-tested
// alone. The web app may not import `packages/scheduler` (D-233), so the
// matrix checks below mirror its `validateSetupMatrixRows` and the server
// still decides (422 `SETUP_MATRIX_INVALID`).

import { addDaysIso, mondayOfIso } from "../../../../components/bucket-grid-paste";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { daysBetween, wallToUtcMs, zoneWall } from "./calendar-lib";
import { shortId } from "../../../../lib/short-id";

// ---- the two addresses -------------------------------------------------------------

/** The board's horizon choices (§4.6). */
export const BOARD_DAYS = [7, 14, 28] as const;
export type BoardDays = (typeof BOARD_DAYS)[number];
export type BoardZoom = "day" | "week";

/** `/planning/schedule` search: the window, the zoom, the lane and the three filters. */
export interface ScheduleSearch {
  readonly from?: string | undefined;
  readonly days?: BoardDays | undefined;
  readonly zoom?: BoardZoom | undefined;
  /** A work centre: the phone's chosen lane, and the desktop's lane filter. */
  readonly wc?: string | undefined;
  readonly order?: string | undefined;
  readonly product?: string | undefined;
  readonly key?: string | undefined;
}

export type CapacityGrain = "week" | "day";

/** `/planning/capacity` search: the grain, the first date of a day window and the draft toggle. */
export interface CapacitySearch {
  readonly grain?: CapacityGrain | undefined;
  readonly from?: string | undefined;
  readonly draft?: boolean | undefined;
}

/** `/planning/settings/setup-matrix` search: the chosen work centre. */
export interface SetupMatrixSearch {
  readonly wc?: string | undefined;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const DATE = /^\d{4}-\d{2}-\d{2}$/u;
/** The longest filter text the address keeps. */
const FILTER_MAX = 64;

function uuid(v: unknown): string | undefined {
  return typeof v === "string" && UUID.test(v) ? v.toLowerCase() : undefined;
}

function isoDate(v: unknown): string | undefined {
  if (typeof v !== "string" || !DATE.test(v)) return undefined;
  const t = Date.parse(`${v}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === v ? v : undefined;
}

function text(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 && trimmed.length <= FILTER_MAX ? trimmed : undefined;
}

/** A shared link with a stale or malformed parameter opens with that parameter dropped. */
export function validateScheduleSearch(search: Record<string, unknown>): ScheduleSearch {
  const days = Number(search["days"]);
  const zoom = search["zoom"];
  return {
    from: isoDate(search["from"]),
    days: (BOARD_DAYS as readonly number[]).includes(days) ? (days as BoardDays) : undefined,
    zoom: zoom === "day" || zoom === "week" ? zoom : undefined,
    wc: uuid(search["wc"]),
    order: text(search["order"]),
    product: text(search["product"]),
    key: text(search["key"]),
  };
}

export function validateCapacitySearch(search: Record<string, unknown>): CapacitySearch {
  const grain = search["grain"];
  const draft = search["draft"];
  return {
    grain: grain === "day" || grain === "week" ? grain : undefined,
    from: isoDate(search["from"]),
    draft: draft === true || draft === "true" ? true : undefined,
  };
}

export function validateSetupMatrixSearch(search: Record<string, unknown>): SetupMatrixSearch {
  return { wc: uuid(search["wc"]) };
}

// ---- time in the facility's zone ---------------------------------------------------

const offsetFormatters = new Map<string, Intl.DateTimeFormat | null>();

/** The zone's offset at an instant, "GMT+5", or "" when the runtime does not know the zone. */
export function zoneOffset(ms: number, timezone: string | null | undefined): string {
  if (typeof timezone !== "string" || timezone.length === 0) return "";
  let fmt = offsetFormatters.get(timezone);
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "shortOffset" });
    } catch {
      fmt = null;
    }
    offsetFormatters.set(timezone, fmt);
  }
  if (fmt === null) return "";
  return fmt.formatToParts(ms).find((p) => p.type === "timeZoneName")?.value ?? "";
}

const stampFormatters = new Map<string, Intl.DateTimeFormat | null>();

function stampFormatter(
  locale: string,
  timezone: string,
  withDate: boolean,
): Intl.DateTimeFormat | null {
  const id = `${locale}|${timezone}|${String(withDate)}`;
  let fmt = stampFormatters.get(id);
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        ...(withDate ? { day: "numeric", month: "short" } : {}),
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
    } catch {
      fmt = null;
    }
    stampFormatters.set(id, fmt);
  }
  return fmt;
}

/**
 * An instant in the facility's zone with its offset, never the browser's
 * (§4's P3-I frame): "12 Oct, 14:30 GMT+5", or "14:30 GMT+5" without the date.
 */
export function zonedStamp(
  iso: string | null | undefined,
  locale: string,
  timezone: string | null | undefined,
  options: { date?: boolean } = {},
): string {
  if (iso === null || iso === undefined) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const zone = typeof timezone === "string" && timezone.length > 0 ? timezone : "UTC";
  const fmt = stampFormatter(locale, zone, options.date ?? true);
  if (fmt === null) return iso;
  const offset = zoneOffset(ms, zone);
  return offset.length > 0 ? `${fmt.format(ms)} ${offset}` : fmt.format(ms);
}

/** The facility's local date of an instant. */
export function localDateOf(ms: number, timezone: string): string {
  return (zoneWall(ms, timezone) ?? new Date(ms).toISOString()).slice(0, 10);
}

/** The facility's today. */
export function todayAt(timezone: string | null | undefined, nowMs = Date.now()): string {
  return facilityTodayIso(nowMs, timezone);
}

/** A window of whole local dates as instants, end exclusive. */
export interface TimeWindow {
  readonly from: string;
  readonly to: string;
  readonly startMs: number;
  readonly endMs: number;
}

export function windowOf(from: string, days: number, timezone: string): TimeWindow {
  const to = addDaysIso(from, days - 1);
  return {
    from,
    to,
    startMs: wallToUtcMs(from, "00:00", timezone),
    endMs: wallToUtcMs(addDaysIso(to, 1), "00:00", timezone),
  };
}

/**
 * The first date the board shows: the address's date held inside the
 * schedule's horizon, else the facility's today held there, so a window never
 * starts before the horizon or runs past its end.
 */
export function boardStart(
  wanted: string | undefined,
  today: string,
  horizon: { start: string; days: number } | undefined,
  days: number,
): string {
  let from = wanted ?? today;
  if (horizon === undefined) return from;
  const last = addDaysIso(horizon.start, horizon.days - 1);
  const latestStart = addDaysIso(last, -(days - 1));
  if (from > latestStart) from = latestStart;
  if (from < horizon.start) from = horizon.start;
  return from;
}

/** Where an interval sits on a window, in per cent of its width; undefined when outside. */
export function spanOf(
  startMs: number,
  endMs: number,
  window: Pick<TimeWindow, "startMs" | "endMs">,
): { left: number; width: number } | undefined {
  const total = window.endMs - window.startMs;
  if (total <= 0) return undefined;
  const s = Math.max(startMs, window.startMs);
  const e = Math.min(endMs, window.endMs);
  if (e < s || (e === s && (startMs < window.startMs || startMs >= window.endMs))) {
    return undefined;
  }
  const left = ((s - window.startMs) / total) * 100;
  const width = ((e - s) / total) * 100;
  return { left: round3(left), width: round3(width) };
}

/**
 * The narrowest a block is drawn, in pixels (§4.6): the board's control floor,
 * the same 24 px every other button in the office area keeps. A two-minute
 * operation on a week's window is 4 px to scale, which nobody can hit.
 */
export const BLOCK_MIN_PX = 24;

/** The milliseconds the block floor is worth on a track `trackPx` wide; 0 before it is measured. */
export function minSpanMs(trackPx: number, window: Pick<TimeWindow, "startMs" | "endMs">): number {
  const total = window.endMs - window.startMs;
  if (!(trackPx > 0) || total <= 0) return 0;
  return (BLOCK_MIN_PX / trackPx) * total;
}

/**
 * Where a block is drawn: its interval clipped to the window and, when that is
 * shorter than `minMs`, widened to it. Against the window's end it widens
 * backwards, so a block never runs past the track that clips it. Undefined
 * when the interval is not on the window (`spanOf`'s rule). Rows stack on this
 * interval, so two short blocks drawn at the floor never cover each other.
 */
export function drawnInterval(
  startMs: number,
  endMs: number,
  window: Pick<TimeWindow, "startMs" | "endMs">,
  minMs: number,
): { startMs: number; endMs: number } | undefined {
  if (spanOf(startMs, endMs, window) === undefined) return undefined;
  let s = Math.max(startMs, window.startMs);
  let e = Math.min(endMs, window.endMs);
  const floor = Math.min(Math.max(0, minMs), window.endMs - window.startMs);
  if (e - s < floor) {
    e = s + floor;
    if (e > window.endMs) {
      e = window.endMs;
      s = e - floor;
    }
  }
  return { startMs: s, endMs: e };
}

/** The room a stretch's label ("NIGHT · 70%") takes to its right, in pixels, with its padding. */
export const PIECE_LABEL_ROOM_PX = 120;

/**
 * Whether a stretch's label reads from the stretch's right edge: when the
 * stretch starts within a label's room of the track's end, a label drawn to
 * its right would run past the track, which clips it. False before the track
 * is measured.
 */
export function labelFromEnd(leftPct: number, trackPx: number): boolean {
  if (!(trackPx > 0)) return false;
  return ((100 - leftPct) / 100) * trackPx < PIECE_LABEL_ROOM_PX;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Each local date of the window with where its midnight sits, for the axis. */
export function dayTicks(
  window: TimeWindow,
  timezone: string,
): { date: string; left: number; width: number }[] {
  const out: { date: string; left: number; width: number }[] = [];
  const days = daysBetween(window.from, window.to) + 1;
  for (let i = 0; i < days; i++) {
    const date = addDaysIso(window.from, i);
    const s = wallToUtcMs(date, "00:00", timezone);
    const e = wallToUtcMs(addDaysIso(date, 1), "00:00", timezone);
    const span = spanOf(s, e, window);
    if (span !== undefined) out.push({ date, ...span });
  }
  return out;
}

/** A stretch of a lane's working time as the board reads it. */
export interface PieceLike {
  readonly start: string;
  readonly end: string;
  readonly shift_code: string | null;
  readonly efficiency_pct: number;
  readonly factor: string;
}

/** A piece that runs below full rate: under 100 % efficiency or under a factor of 1. */
export function isSlowPiece(piece: PieceLike): boolean {
  return piece.efficiency_pct < 100 || Number(piece.factor) < 1;
}

/**
 * The piece's label, "Night shift · 70%": the shift's name where the facility
 * gave it one (else its code), and its effective rate.
 */
export function pieceLabel(
  piece: PieceLike,
  shiftNames: ReadonlyMap<string, string> = new Map(),
): string {
  const rate = Math.round(piece.efficiency_pct * Number(piece.factor) * 100) / 100;
  const pct = `${String(rate)}%`;
  if (piece.shift_code === null || piece.shift_code.length === 0) return pct;
  return `${shiftNames.get(piece.shift_code) ?? piece.shift_code} · ${pct}`;
}

/** The instants inside [start, end) that no piece covers, merged and sorted. */
export function closedGaps(
  startMs: number,
  endMs: number,
  pieces: readonly PieceLike[],
): { start: number; end: number }[] {
  const open = pieces
    .map((p) => ({ s: Date.parse(p.start), e: Date.parse(p.end) }))
    .filter((p) => Number.isFinite(p.s) && Number.isFinite(p.e) && p.e > startMs && p.s < endMs)
    .sort((a, b) => a.s - b.s);
  const gaps: { start: number; end: number }[] = [];
  let cursor = startMs;
  for (const p of open) {
    if (p.s > cursor) gaps.push({ start: cursor, end: Math.min(p.s, endMs) });
    cursor = Math.max(cursor, p.e);
    if (cursor >= endMs) break;
  }
  if (cursor < endMs) gaps.push({ start: cursor, end: endMs });
  return gaps.filter((g) => g.end > g.start);
}

/** Effective minutes a piece gives per wall minute: its efficiency times its factor. */
export function pieceRate(piece: PieceLike): number {
  return (piece.efficiency_pct / 100) * Number(piece.factor);
}

/**
 * A lane's utilization over the visible window (§4.6): the effective minutes
 * of placed work that fall in the window over the effective capacity the
 * window holds, one decimal; undefined when the window holds no capacity.
 * Work consumes only open time, at each piece's rate (§3.5 step 4), so each
 * operation's share is its overlap with each piece times that piece's rate.
 */
export function windowUtilization(
  ops: readonly { readonly startMs: number; readonly endMs: number }[],
  pieces: readonly PieceLike[],
  window: Pick<TimeWindow, "startMs" | "endMs">,
): number | undefined {
  const overlap = (s1: number, e1: number, s2: number, e2: number) =>
    Math.max(0, Math.min(e1, e2) - Math.max(s1, s2));
  let capacity = 0;
  let used = 0;
  for (const piece of pieces) {
    const ps = Date.parse(piece.start);
    const pe = Date.parse(piece.end);
    if (!Number.isFinite(ps) || !Number.isFinite(pe)) continue;
    const rate = pieceRate(piece);
    capacity += overlap(ps, pe, window.startMs, window.endMs) * rate;
    for (const op of ops) {
      const s = Math.max(op.startMs, window.startMs);
      const e = Math.min(op.endMs, window.endMs);
      if (e > s) used += overlap(ps, pe, s, e) * rate;
    }
  }
  if (capacity <= 0) return undefined;
  return Math.round((used / capacity) * 1000) / 10;
}

/** Minutes between two instants, whole. */
export function wallMinutesOf(start: string | null, end: string | null): number | undefined {
  if (start === null || end === null) return undefined;
  const ms = Date.parse(end) - Date.parse(start);
  return Number.isFinite(ms) ? Math.round(ms / 60_000) : undefined;
}

/**
 * Operations that overlap on one lane (two in-progress holds, §3.5 step 1)
 * stacked into rows, each in the first row it fits, by start then reference.
 */
export function stackRows(
  ops: readonly { readonly op_ref: string; readonly startMs: number; readonly endMs: number }[],
): { rows: Map<string, number>; count: number } {
  const sorted = [...ops].sort((a, b) =>
    a.startMs !== b.startMs ? a.startMs - b.startMs : a.op_ref < b.op_ref ? -1 : 1,
  );
  const ends: number[] = [];
  const rows = new Map<string, number>();
  for (const op of sorted) {
    let row = ends.findIndex((end) => end <= op.startMs);
    if (row < 0) {
      row = ends.length;
      ends.push(op.endMs);
    } else {
      ends[row] = op.endMs;
    }
    rows.set(op.op_ref, row);
  }
  return { rows, count: Math.max(1, ends.length) };
}

/** The lane's name as the board heads it: "EXT-1 · Extruder 1", or its code alone. */
export function laneName(
  lane: { readonly code: string; readonly name: string } | undefined,
  id: string,
): string {
  if (lane === undefined) return shortId(id);
  return lane.name.length > 0 && lane.name !== lane.code
    ? `${lane.code} · ${lane.name}`
    : lane.code;
}

// ---- the board's filters -----------------------------------------------------------

export interface BoardFilters {
  readonly order?: string | undefined;
  readonly product?: string | undefined;
  readonly key?: string | undefined;
}

interface Filterable {
  readonly label: { readonly order_no: string | null };
  readonly product_sku: string | null;
  readonly product_name: string | null;
  readonly setup_key: string | null;
}

function contains(haystack: string | null, needle: string): boolean {
  return haystack?.toLowerCase().includes(needle.toLowerCase()) === true;
}

/** Whether an operation passes the three filters: order number, product and setup key. */
export function passesFilters(op: Filterable, filters: BoardFilters): boolean {
  if (filters.order !== undefined && !contains(op.label.order_no, filters.order)) return false;
  if (
    filters.product !== undefined &&
    !contains(op.product_sku, filters.product) &&
    !contains(op.product_name, filters.product)
  ) {
    return false;
  }
  if (filters.key !== undefined && !contains(op.setup_key, filters.key)) return false;
  return true;
}

export function hasFilters(filters: BoardFilters): boolean {
  return filters.order !== undefined || filters.product !== undefined || filters.key !== undefined;
}

// ---- an operation's facts ----------------------------------------------------------

/** A setup key for reading, its pairs joined by " · ": `xs:2.5 · colour:WH`. */
export function keyText(key: string | null): string {
  return key === null ? "" : key.split("/").join(" · ");
}

/**
 * Days to spare before the order's due date ends (positive), or days late
 * (negative), from the local date the operation ends on; undefined without a
 * due date or an end.
 */
export function slackDays(
  plannedEnd: string | null,
  dueDate: string | null,
  timezone: string,
): number | undefined {
  if (plannedEnd === null || dueDate === null) return undefined;
  const ms = Date.parse(plannedEnd);
  if (!Number.isFinite(ms)) return undefined;
  // An end exactly at midnight belongs to the date before it (D-408 ruling 12(d)).
  const endDate = localDateOf(ms - 1, timezone);
  return daysBetween(endDate, dueDate);
}

// ---- runs --------------------------------------------------------------------------

/** Minutes and seconds a run took, or undefined while it runs. */
export function runDuration(run: {
  readonly started_at: string;
  readonly finished_at: string | null;
}): { minutes: number; seconds: number } | undefined {
  if (run.finished_at === null) return undefined;
  const ms = Date.parse(run.finished_at) - Date.parse(run.started_at);
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const total = Math.round(ms / 1000);
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

/** How a finished run ended, for the solve's line: which of §3.5's outcomes it was. */
export type RunOutcome =
  "wrote" | "short_circuit" | "stale_base" | "empty" | "auto_published" | "failed" | "running";

export function runOutcome(run: {
  readonly status: "running" | "succeeded" | "failed";
  readonly schedule_id: string | null;
  readonly short_circuit_schedule_id: string | null;
  readonly stats: {
    readonly stale_base?: boolean | undefined;
    readonly empty?: boolean | undefined;
    readonly auto_published?: boolean | undefined;
  };
}): RunOutcome {
  if (run.status === "running") return "running";
  if (run.status === "failed") return "failed";
  if (run.stats.stale_base === true) return "stale_base";
  if (run.stats.empty === true) return "empty";
  if (run.short_circuit_schedule_id !== null) return "short_circuit";
  if (run.stats.auto_published === true) return "auto_published";
  return "wrote";
}

// ---- capacity ----------------------------------------------------------------------

export interface LoadRowLike {
  readonly work_center_id: string;
  readonly period_start: string;
  readonly capacity_minutes: number;
  readonly setup_minutes: number;
  readonly run_minutes: number;
  readonly due_minutes: number;
  readonly proposed_minutes: number;
  readonly overload_minutes: number;
}

/** Rows grouped by work centre, each group's periods in order. */
export function loadByWorkCenter<R extends LoadRowLike>(rows: readonly R[]): Map<string, R[]> {
  const out = new Map<string, R[]>();
  for (const row of rows) {
    const list = out.get(row.work_center_id) ?? [];
    list.push(row);
    out.set(row.work_center_id, list);
  }
  for (const list of out.values()) list.sort((a, b) => (a.period_start < b.period_start ? -1 : 1));
  return out;
}

/** The figures one bar draws, each in per cent of the bar's scale. */
export interface BarGeometry {
  readonly setup: number;
  readonly run: number;
  readonly proposed: number;
  readonly capacity: number;
  readonly due: number;
}

/** A row's bar: placed setup and run, proposed above them, the capacity mark and the due dot. */
export function barOf(row: LoadRowLike): BarGeometry {
  const placed = row.setup_minutes + row.run_minutes;
  const scale = Math.max(placed + row.proposed_minutes, row.capacity_minutes, row.due_minutes, 1);
  const p = (n: number) => round3((n / scale) * 100);
  return {
    setup: p(row.setup_minutes),
    run: p(row.run_minutes),
    proposed: p(row.proposed_minutes),
    capacity: p(row.capacity_minutes),
    due: p(row.due_minutes),
  };
}

/** The waiting draft's load less the published one's, per work centre and period. */
export function loadDelta(
  draft: readonly LoadRowLike[],
  published: readonly LoadRowLike[],
): LoadRowLike[] {
  const key = (r: LoadRowLike) => `${r.work_center_id}|${r.period_start}`;
  const base = new Map(published.map((r) => [key(r), r]));
  const seen = new Set<string>();
  const out: LoadRowLike[] = [];
  const zero = (wc: string, period: string): LoadRowLike => ({
    work_center_id: wc,
    period_start: period,
    capacity_minutes: 0,
    setup_minutes: 0,
    run_minutes: 0,
    due_minutes: 0,
    proposed_minutes: 0,
    overload_minutes: 0,
  });
  const diff = (a: LoadRowLike, b: LoadRowLike): LoadRowLike => ({
    work_center_id: a.work_center_id,
    period_start: a.period_start,
    capacity_minutes: a.capacity_minutes - b.capacity_minutes,
    setup_minutes: a.setup_minutes - b.setup_minutes,
    run_minutes: a.run_minutes - b.run_minutes,
    due_minutes: a.due_minutes - b.due_minutes,
    proposed_minutes: a.proposed_minutes - b.proposed_minutes,
    overload_minutes: a.overload_minutes - b.overload_minutes,
  });
  for (const row of draft) {
    const k = key(row);
    seen.add(k);
    out.push(diff(row, base.get(k) ?? zero(row.work_center_id, row.period_start)));
  }
  for (const row of published) {
    if (!seen.has(key(row))) out.push(diff(zero(row.work_center_id, row.period_start), row));
  }
  return out;
}

/** The Monday of a date's ISO week. */
export function weekOf(date: string): string {
  return mondayOfIso(date);
}

// ---- the setup matrix --------------------------------------------------------------

/** The facet grammar (§2.15): lower-case, up to 32 characters, or `_` for a free-form key. */
export const FACET_RE = /^[a-z][a-z0-9_]{0,31}$/u;
export const VALUE_RE = /^[A-Za-z0-9._+-]{1,32}$/u;
export const OPAQUE_FACET = "_";
export const ANY_VALUE = "*";
export const ABSENT_VALUE = "-";
export const MAX_OPAQUE_LENGTH = 256;
export const MAX_MATRIX_MINUTES = 1440;
/** The most rows one replace carries (contracts `SETUP_MATRIX_MAX_ROWS`). */
export const SETUP_MATRIX_MAX_ROWS = 2000;

export type MatrixRowReason =
  "facet_malformed" | "value_malformed" | "same_value" | "duplicate_row" | "minutes_out_of_range";

export const MATRIX_ROW_REASONS: readonly MatrixRowReason[] = [
  "facet_malformed",
  "value_malformed",
  "same_value",
  "duplicate_row",
  "minutes_out_of_range",
];

/** One row as the screen edits it, minutes still text. */
export interface MatrixDraft {
  readonly id: string;
  readonly facet: string;
  readonly from: string;
  readonly to: string;
  readonly minutes: string;
}

/** Whole minutes from the box, or NaN when it is not a whole number. */
export function minutesOf(text: string): number {
  const raw = text.trim();
  return /^\d{1,6}$/u.test(raw) ? Number(raw) : Number.NaN;
}

function valueOk(facet: string, value: string): boolean {
  if (value === ANY_VALUE) return true;
  if (facet === OPAQUE_FACET) return value.length >= 1 && value.length <= MAX_OPAQUE_LENGTH;
  return VALUE_RE.test(value);
}

/**
 * The reasons each row would be refused, by its index in the order sent: the
 * kernel's `validateSetupMatrixRows` rule for rule, so a row the server would
 * refuse is refused here first (M03-S20).
 */
export function checkMatrix(rows: readonly MatrixDraft[]): Map<number, MatrixRowReason[]> {
  const seen = new Set<string>();
  const out = new Map<number, MatrixRowReason[]>();
  rows.forEach((row, index) => {
    const reasons: MatrixRowReason[] = [];
    if (row.facet !== OPAQUE_FACET && !FACET_RE.test(row.facet)) reasons.push("facet_malformed");
    if (!valueOk(row.facet, row.from) || !valueOk(row.facet, row.to)) {
      reasons.push("value_malformed");
    }
    if (row.from !== ANY_VALUE && row.from === row.to) reasons.push("same_value");
    const key = `${row.facet}\u0000${row.from}\u0000${row.to}`;
    if (seen.has(key)) reasons.push("duplicate_row");
    seen.add(key);
    const minutes = minutesOf(row.minutes);
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > MAX_MATRIX_MINUTES) {
      reasons.push("minutes_out_of_range");
    }
    if (reasons.length > 0) out.set(index, reasons);
  });
  return out;
}

/**
 * A row names a value no active routing step on the work centre uses: the
 * server's `unused` rule, applied to an unsaved row too, so the lint reads the
 * same before and after a save.
 */
export function isUnusedRow(
  row: Pick<MatrixDraft, "facet" | "from" | "to">,
  inUse: Readonly<Record<string, readonly string[]>>,
): boolean {
  const values = inUse[row.facet];
  if (values === undefined) return true;
  return [row.from, row.to]
    .filter((v) => v !== ANY_VALUE && v !== ABSENT_VALUE)
    .some((v) => !values.includes(v));
}

/** The facets in the order the screen groups them: alphabetical, `_` last. */
export function facetOrder(facets: Iterable<string>): string[] {
  return [...new Set(facets)].sort((a, b) => {
    if (a === b) return 0;
    if (a === OPAQUE_FACET) return 1;
    if (b === OPAQUE_FACET) return -1;
    return a < b ? -1 : 1;
  });
}

/**
 * The rows in the order a save sends them: grouped by facet in the screen's
 * order, each group in the order it was typed, so a 422's row index names the
 * row the screen shows.
 */
export function sendOrder(rows: readonly MatrixDraft[]): MatrixDraft[] {
  const facets = facetOrder(rows.map((r) => r.facet));
  return facets.flatMap((facet) => rows.filter((r) => r.facet === facet));
}

/** Whether two sets of rows say the same thing, ignoring order and local ids. */
export function sameMatrix(a: readonly MatrixDraft[], b: readonly MatrixDraft[]): boolean {
  const canon = (rows: readonly MatrixDraft[]) =>
    rows
      .map((r) => `${r.facet}\u0000${r.from}\u0000${r.to}\u0000${r.minutes.trim()}`)
      .sort()
      .join("\u0001");
  return canon(a) === canon(b);
}
